/**
 * @fileoverview Module Event Bus for inter-module communication
 * 
 * The ModuleEventBus provides a centralized event system that allows modules
 * to communicate with each other through events while maintaining loose coupling.
 */

import { ModuleEventType } from '../types/index.js';

/**
 * Event bus for inter-module communication
 * 
 * @class ModuleEventBus
 */
export class ModuleEventBus {
  /**
   * Create a new ModuleEventBus instance
   */
  constructor() {
    /** @type {Map<string, Set<function>>} */
    this.subscribers = new Map();
    
    /** @type {Map<string, Set<function>>} */
    this.permissionFilters = new Map();
    
    /** @type {ModuleEvent[]} */
    this.eventHistory = [];
    
    /** @type {number} */
    this.maxHistorySize = 1000;
    
    /** @type {boolean} */
    this.debugMode = false;
  }

  /**
   * Emit an event to all subscribers
   * 
   * @param {ModuleEvent} event - The event to emit
   */
  emit(event) {
    try {
      // Validate event structure
      if (!this._validateEvent(event)) {
        console.error('Invalid event structure:', event);
        return;
      }

      // Add timestamp if not present
      if (!event.timestamp) {
        event.timestamp = Date.now();
      }

      // Add to history
      this._addToHistory(event);

      // Debug logging
      if (this.debugMode) {
        console.debug('ModuleEventBus: Emitting event', event);
      }

      // Get subscribers for this event type
      const eventSubscribers = this.subscribers.get(event.type) || new Set();
      const globalSubscribers = this.subscribers.get('*') || new Set();
      
      // Combine all subscribers
      const allSubscribers = new Set([...eventSubscribers, ...globalSubscribers]);

      // Emit to all subscribers
      allSubscribers.forEach(callback => {
        try {
          // Check permission filters
          if (this._checkPermissionFilter(callback, event)) {
            callback(event);
          }
        } catch (error) {
          console.error('Error in event subscriber:', error);
        }
      });

    } catch (error) {
      console.error('Error emitting event:', error);
    }
  }

  /**
   * Subscribe to events of a specific type
   * 
   * @param {string} eventType - Event type to subscribe to (use '*' for all events)
   * @param {function(ModuleEvent): void} callback - Callback function
   * @param {Object} [options] - Subscription options
   * @param {string[]} [options.permissions] - Required permissions to receive events
   * @param {string} [options.sourceFilter] - Only receive events from specific source
   * @returns {function} Unsubscribe function
   */
  subscribe(eventType, callback, options = {}) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    // Initialize subscribers set for this event type
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }

    // Create wrapped callback with filters
    const wrappedCallback = (event) => {
      // Apply source filter if specified
      if (options.sourceFilter && event.source !== options.sourceFilter) {
        return;
      }

      callback(event);
    };

    // Store permission filters if specified
    if (options.permissions && options.permissions.length > 0) {
      this.permissionFilters.set(wrappedCallback, new Set(options.permissions));
    }

    // Add subscriber
    this.subscribers.get(eventType).add(wrappedCallback);

    if (this.debugMode) {
      console.debug(`ModuleEventBus: Subscribed to '${eventType}' events`);
    }

    // Return unsubscribe function
    return () => this.unsubscribe(eventType, wrappedCallback);
  }

  /**
   * Unsubscribe from events
   * 
   * @param {string} eventType - Event type to unsubscribe from
   * @param {function} callback - Callback function to remove
   * @returns {boolean} True if callback was removed, false if not found
   */
  unsubscribe(eventType, callback) {
    const eventSubscribers = this.subscribers.get(eventType);
    if (!eventSubscribers) {
      return false;
    }

    const removed = eventSubscribers.delete(callback);
    
    // Clean up permission filters
    this.permissionFilters.delete(callback);

    // Clean up empty subscriber sets
    if (eventSubscribers.size === 0) {
      this.subscribers.delete(eventType);
    }

    if (this.debugMode && removed) {
      console.debug(`ModuleEventBus: Unsubscribed from '${eventType}' events`);
    }

    return removed;
  }

  /**
   * Clear all subscriptions and history
   */
  clear() {
    this.subscribers.clear();
    this.permissionFilters.clear();
    this.eventHistory = [];
    
    if (this.debugMode) {
      console.debug('ModuleEventBus: All subscriptions and history cleared');
    }
  }

  /**
   * Get event history
   * 
   * @param {Object} [filters] - Filters to apply
   * @param {string} [filters.type] - Filter by event type
   * @param {string} [filters.source] - Filter by source module
   * @param {number} [filters.since] - Filter by timestamp (events after this time)
   * @param {number} [filters.limit] - Limit number of results
   * @returns {ModuleEvent[]} Filtered event history
   */
  getHistory(filters = {}) {
    let events = [...this.eventHistory];

    // Apply filters
    if (filters.type) {
      events = events.filter(event => event.type === filters.type);
    }

    if (filters.source) {
      events = events.filter(event => event.source === filters.source);
    }

    if (filters.since) {
      events = events.filter(event => event.timestamp >= filters.since);
    }

    // Apply limit
    if (filters.limit && filters.limit > 0) {
      events = events.slice(-filters.limit);
    }

    return events;
  }

  /**
   * Get statistics about the event bus
   * 
   * @returns {Object} Event bus statistics
   */
  getStats() {
    const subscriberCounts = {};
    let totalSubscribers = 0;

    this.subscribers.forEach((subscribers, eventType) => {
      subscriberCounts[eventType] = subscribers.size;
      totalSubscribers += subscribers.size;
    });

    return {
      totalSubscribers,
      subscribersByType: subscriberCounts,
      eventTypes: Array.from(this.subscribers.keys()),
      historySize: this.eventHistory.length,
      maxHistorySize: this.maxHistorySize,
      debugMode: this.debugMode
    };
  }

  /**
   * Enable or disable debug mode
   * 
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = Boolean(enabled);
    console.log(`ModuleEventBus debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set maximum event history size
   * 
   * @param {number} size - Maximum number of events to keep in history
   */
  setMaxHistorySize(size) {
    if (size < 0) {
      throw new Error('Max history size must be non-negative');
    }

    this.maxHistorySize = size;
    
    // Trim history if needed
    if (this.eventHistory.length > size) {
      this.eventHistory = this.eventHistory.slice(-size);
    }
  }

  /**
   * Create a scoped event bus for a specific module
   * 
   * @param {string} moduleId - Module ID for scoping
   * @param {string[]} [permissions] - Module permissions
   * @returns {Object} Scoped event bus interface
   */
  createScopedBus(moduleId, permissions = []) {
    return {
      emit: (event) => {
        const scopedEvent = {
          ...event,
          source: event.source || moduleId, // Only set source if not already provided
          timestamp: event.timestamp || Date.now()
        };
        this.emit(scopedEvent);
      },

      subscribe: (eventType, callback, options = {}) => {
        return this.subscribe(eventType, callback, {
          ...options,
          permissions: permissions
        });
      },

      unsubscribe: (eventType, callback) => {
        return this.unsubscribe(eventType, callback);
      },

      getHistory: (filters = {}) => {
        return this.getHistory({
          ...filters,
          source: moduleId
        });
      }
    };
  }

  // Private helper methods

  /**
   * Validate event structure
   * @private
   */
  _validateEvent(event) {
    if (!event || typeof event !== 'object') {
      return false;
    }

    if (!event.type || typeof event.type !== 'string') {
      return false;
    }

    if (!event.source || typeof event.source !== 'string') {
      return false;
    }

    return true;
  }

  /**
   * Add event to history
   * @private
   */
  _addToHistory(event) {
    this.eventHistory.push({ ...event });

    // Trim history if it exceeds max size
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
    }
  }

  /**
   * Check permission filter for callback
   * @private
   */
  _checkPermissionFilter(callback, event) {
    const requiredPermissions = this.permissionFilters.get(callback);
    if (!requiredPermissions || requiredPermissions.size === 0) {
      return true; // No permission filter
    }

    // For now, we'll assume all events can be received
    // In a real implementation, you'd check against user permissions
    return true;
  }
}

// Create and export a singleton instance
export const moduleEventBus = new ModuleEventBus();
